import React, { useState, useEffect } from "react";
import { LogOut, Menu, AlertTriangle, CheckCircle } from "lucide-react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../../store/slices/Auth/OtpSlice";


interface HeaderProps {
    onMenuClick: () => void;
}

interface CustomModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    type: 'warning' | 'success';
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
}

const CustomModal: React.FC<CustomModalProps> = ({
                                                     isOpen,
                                                     onClose,
                                                     onConfirm,
                                                     type,
                                                     title,
                                                     message,
                                                     confirmText,
                                                     cancelText
                                                 }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div
            className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200 ${
                isVisible ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
                animation: 'fadeIn 0.2s ease-out'
            }}
        >
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-xs"
                onClick={onClose}
            />

            {/* Modal */}
            <div
                className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all"
                style={{
                    animation: 'slideUp 0.3s ease-out'
                }}
            >
                {/* Content */}
                <div className="p-6 text-center">
                    {/* Icon */}
                    <div className="mx-auto w-16 h-16 mb-4 flex items-center justify-center rounded-full">
                        {type === 'warning' ? (
                            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                                <AlertTriangle className="w-8 h-8 text-amber-600" />
                            </div>
                        ) : (
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                        )}
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {title}
                    </h3>

                    {/* Message */}
                    <p className="text-gray-600 mb-6">
                        {message}
                    </p>

                    {/* Buttons */}
                    {type === 'warning' ? (
                        <div className="flex gap-3">
                            <button
                                onClick={onClose}
                                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
                            >
                                {cancelText || 'Ləğv et'}
                            </button>
                            <button
                                onClick={onConfirm}
                                className="flex-1 px-4 py-3 bg-colorpink text-white font-medium rounded-xl transition-all shadow-lg hover:shadow-xl"
                            >
                                {confirmText || 'Təsdiq et'}
                            </button>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [firstName, setFirstName] = useState<string>("İstifadəçi");
  const [lastName, setLastName] = useState<string>("");

    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('selectedLanguage') || 'az';
    });


    const translations = {
        az: {
            personalCabinet: "Şəxsi kabinet",
            welcome: "Xoş gəldin",
            logout: "Çıxış et",
            logoutConfirmTitle: "Çıxmaq istədiyinizdən əminsiniz?",
            logoutConfirmMessage: "Hesabınızdan çıxış edəcəksiniz",
            logoutConfirmButton: "Bəli, çıxış et",
            cancelButton: "Ləğv et",
            logoutSuccessTitle: "Çıxış edildi!",
            logoutSuccessMessage: "Hesabınızdan uğurla çıxış etdiniz",
            user: "İstifadəçi"
        },
        ru: {
            personalCabinet: "Личный кабинет",
            welcome: "Добро пожаловать",
            logout: "Выйти",
            logoutConfirmTitle: "Вы уверены, что хотите выйти?",
            logoutConfirmMessage: "Вы выйдете из своей учетной записи",
            logoutConfirmButton: "Да, выйти",
            cancelButton: "Отмена",
            logoutSuccessTitle: "Выход выполнен!",
            logoutSuccessMessage: "Вы успешно вышли из учетной записи",
            user: "Пользователь"
        },
        en: {
            personalCabinet: "Personal Cabinet",
            welcome: "Welcome",
            logout: "Log out",
            logoutConfirmTitle: "Are you sure you want to log out?",
            logoutConfirmMessage: "You will be logged out of your account",
            logoutConfirmButton: "Yes, log out",
            cancelButton: "Cancel",
            logoutSuccessTitle: "Logged out!",
            logoutSuccessMessage: "You have successfully logged out",
            user: "User"
        }
    };

    const t = translations[currentLanguage as keyof typeof translations];

  useEffect(() => {
    const userInfo = localStorage.getItem('user_info');
    if (userInfo) {
      const decoded = JSON.parse(userInfo);
      setFirstName(decoded.first_name || t.user);
      setLastName(decoded.last_name || "");
    }
  }, [t.user]);

    const handleLogoutClick = () => {
        setShowLogoutModal(true);
    };

    const handleLogoutConfirm = () => {
        setShowLogoutModal(false);
        dispatch(logout());

        // Show success modal
        setShowSuccessModal(true);

        // Navigate after delay
        setTimeout(() => {
            setShowSuccessModal(false);
            navigate("/login");
        }, 1500);
    };

    const handleLogoutCancel = () => {
        setShowLogoutModal(false);
    };

    return (
        <>
            <style dangerouslySetInnerHTML={{__html: `
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}} />

            <header className="bg-white border-b border-gray-200 px-2 lg:px-6 py-4">
                <div className="flex justify-between items-center">
                    <div className="flex items-center">
                        <button
                            onClick={onMenuClick}
                            className="lg:hidden text-gray-600 hover:text-gray-900 p-2 -ml-2 rounded-lg  transition-colors"
                            aria-label="Menyunu aç"
                        >
                            <Menu className="w-6 h-6" />
                        </button>

                        <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 transition-colors">
                            {t.personalCabinet}
                        </h1>
                    </div>

                    <div className="flex items-center space-x-2 sm:space-x-4">
                        {/* Desktop Welcome Text */}
                        <div className="hidden sm:flex items-center space-x-2">
                            <span
                                className="text-sm lg:text-base text-gray-700 cursor-pointer hover:text-colorpink transition-colors"
                                onClick={() => navigate('/myprofile')}
                            >
                                {t.welcome}, <span className="font-medium">{firstName} {lastName?.slice(0, 1)}.</span>
                            </span>
                        </div>

                      <div className="sm:hidden flex items-center space-x-1">
                       <span className="text-sm font-medium text-gray-700 hover:text-colorpink"
                             onClick={() => navigate('/myprofile')}>
                          {firstName} {lastName?.slice(0, 1)}.
                       </span>
                         </div>

                        {/* Logout Button */}
                        <button
                            className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 text-gray-600 hover:text-red-600 bg-red-50 rounded-lg transition-all duration-200 group"
                            onClick={handleLogoutClick}
                            aria-label={t.logout}
                        >
                            <LogOut className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Logout Confirmation Modal */}
            <CustomModal
                isOpen={showLogoutModal}
                onClose={handleLogoutCancel}
                onConfirm={handleLogoutConfirm}
                type="warning"
                title={t.logoutConfirmTitle}
                message={t.logoutConfirmMessage}
                confirmText={t.logoutConfirmButton}
                cancelText={t.cancelButton}
            />

            {/* Success Modal */}
            <CustomModal
                isOpen={showSuccessModal}
                onClose={() => {}}
                onConfirm={() => {}}
                type="success"
                title={t.logoutSuccessTitle}
                message={t.logoutSuccessMessage}
            />
        </>
    );
};

export default Header;